/**
 * Gameplay numbers that are not in the JSON data (specs/game-mechanics.md
 * 2.2, 2.4, 2.6, 2.7 and specs/dog-attributes.md 2). Pure logic: no PixiJS,
 * GSAP, DOM or JSON imports, so tests can import it directly.
 */

export const BASE_RADIUS = 60;
export const GRASS_TOP_Y = 470;
export const GRASS_LINE_Y = 487;
export const POINTS_PER_RECEIPT = 100;
// Scoring (Bosco, 6 Oct 2026; chosen in specs/sim/scoring.txt, the level
// clear bonus resized in specs/sim/clear-bonus.txt). A level is cleared the
// moment its need is caught, and the time left is the skill bonus: 1 point per
// hundredth of a second left, times the level number (so 100 a second on level
// 1 and 600 on level 6; the card reads the clock's own digits). Clearing a
// level also scores the LEVEL CLEAR BONUS, LEVEL_CLEAR_POINTS x the level
// number: a round 2,000 / 4,000 / ... / 12,000, so the board can rank by score
// alone and a run that cleared more levels still outscores one that cleared
// fewer in all but about 1 in 100,000 modelled pairs, while the time bonus is
// about 40% of a good winning score (it was under 20% with 6,750 x L).
// Hundredths keep ties rare.
export const BONUS_POINTS_PER_HUNDREDTH = 1;
export const LEVEL_CLEAR_POINTS = 2000;
// 1.5s, down from 3s after the 6 Oct difficulty audit (specs/sim/audit.txt):
// the clock runs through a rest, and 3s rests took a third to half of it
export const TIRED_SECONDS = 1.5;
export const INTER_WAVE_GAP_SECONDS = 2.2;
export const PASS_RATIO = 0.65;

/** Per-bar-value tables, indexed by bar value minus 1. */
export const STAT_TABLES = {
  pounceMs: [180, 150, 120, 90, 60],
  jumpShrink: [0.24, 0.19, 0.14, 0.09, 0.04],
  staminaClicks: [2, 3, 4, 6, 7],
  accuracyMult: [0.90, 0.95, 1.00, 1.05, 1.10],
};

/**
 * Look up one bar value in a table, rejecting anything but a whole 1-5.
 * @param {string} name stat name, for the error message
 * @param {number} value bar value
 * @param {number[]} table
 * @returns {number}
 */
function lookup(name, value, table) {
  if (!Number.isInteger(value) || value < 1 || value > table.length) {
    throw new RangeError(`${name} must be a whole number from 1 to ${table.length}, got ${value}`);
  }
  return table[value - 1];
}

/**
 * Turn a dog's four bars into gameplay parameters.
 * @param {{speed: number, jumping: number, stamina: number, accuracy: number}} stats bars, 1-5
 * @returns {{pounceMs: number, jumpShrink: number, staminaClicks: number, accuracyMult: number}}
 */
export function dogParams(stats) {
  const { speed, jumping, stamina, accuracy } = stats || {};
  return {
    pounceMs: lookup('speed', speed, STAT_TABLES.pounceMs),
    jumpShrink: lookup('jumping', jumping, STAT_TABLES.jumpShrink),
    staminaClicks: lookup('stamina', stamina, STAT_TABLES.staminaClicks),
    accuracyMult: lookup('accuracy', accuracy, STAT_TABLES.accuracyMult),
  };
}

/**
 * Hit radius for a receipt at height y: 0 shrink at the grass line, full
 * shrink at the top of the sky.
 * @param {{accuracyMult: number, jumpShrink: number}} params from dogParams
 * @param {number} y receipt y at the moment the pounce resolves
 * @returns {number}
 */
export function hitRadius(params, y) {
  const h = Math.min(1, Math.max(0, (GRASS_LINE_Y - y) / GRASS_LINE_Y));
  return BASE_RADIUS * params.accuracyMult * (1 - params.jumpShrink * h);
}

/**
 * Receipts needed to pass: the smallest whole number above 65% of total.
 * Integer maths so 0.65 * 20 style float error cannot change the answer.
 * @param {number} total receipts in the level
 * @returns {number}
 */
export function needFor(total) {
  return Math.floor((total * Math.round(PASS_RATIO * 100)) / 100) + 1;
}

/**
 * Whole hundredths of a second banked from the clock, rounded down; never
 * below 0. The small epsilon keeps 2.3 (stored as 2.2999...) at 230.
 * @param {number} secondsLeft
 * @returns {number}
 */
export function bonusHundredthsFor(secondsLeft) {
  return Math.max(0, Math.floor(secondsLeft * 100 + 1e-6));
}

/**
 * The most hundredths a passed level can bank: its whole clock less one
 * hundredth. A shot is only fired while the clock runs (Game.canShoot), and the
 * catching pounce lands at least a frame later, so a level is never cleared
 * with its full clock left. submit_run should refuse anything above this.
 * @param {number} clockSeconds the level's clock
 * @returns {number}
 */
export function maxBonusHundredths(clockSeconds) {
  return clockSeconds * 100 - 1;
}

/**
 * What one passed level adds on top of its receipts, in points.
 * @param {number} level level number, 1 to 6
 * @param {number} bonusHundredths hundredths of a second left at the clear
 * @returns {{time: number, clear: number}} the time bonus and the level clear bonus
 */
export function levelBonusPoints(level, bonusHundredths) {
  return {
    time: BONUS_POINTS_PER_HUNDREDTH * bonusHundredths * level,
    clear: LEVEL_CLEAR_POINTS * level,
  };
}

/**
 * Run score: 100 x receipts, plus on each passed level its time bonus and
 * level clear bonus (levelBonusPoints). Levels are numbered from 1 in order; the
 * first `levelsPassed` of them were passed.
 * @param {number[]} receiptsByLevel receipts caught per level reached
 * @param {number[]} bonusByLevel hundredths of a second banked per level reached
 * @param {number} levelsPassed levels passed
 * @returns {number}
 */
export function scoreFor(receiptsByLevel, bonusByLevel, levelsPassed) {
  let score = 0;
  receiptsByLevel.forEach((receipts, i) => {
    score += POINTS_PER_RECEIPT * receipts;
    if (i < levelsPassed) {
      const bonus = levelBonusPoints(i + 1, bonusByLevel[i] || 0);
      score += bonus.time + bonus.clear;
    }
  });
  return score;
}

export const HIT_WORDS = ['GOTCHA!', 'SNAP!', 'CHOMP!', 'CRUNCH!', 'WOOF!'];

/**
 * Pick a hit word that differs from the previous one.
 * @param {string|null|undefined} previous last word shown
 * @param {() => number} [rng] returns a number in [0, 1)
 * @returns {string}
 */
export function pickHitWord(previous, rng = Math.random) {
  const pool = HIT_WORDS.filter((w) => w !== previous);
  return pool[Math.min(pool.length - 1, Math.floor(rng() * pool.length))];
}
